import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    AlertCircle, ArrowRight, CheckCircle, Clock, Zap, Bell
} from 'lucide-react';

const PRIORITY_STYLES = {
    high: { bg: 'bg-red-50 border-red-200', icon: 'text-red-500', badge: 'bg-red-100 text-red-700' },
    medium: { bg: 'bg-amber-50 border-amber-200', icon: 'text-amber-500', badge: 'bg-amber-100 text-amber-700' },
    low: { bg: 'bg-blue-50 border-blue-200', icon: 'text-blue-500', badge: 'bg-blue-100 text-blue-700' },
};

export function ActionItems() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        API.get('/autonomy/action-items').then(r => setData(r.data)).catch(() => {});
    }, []);

    if (!data || data.items.length === 0) return null;

    return (
        <div className="space-y-2 mb-6" data-testid="action-items">
            <div className="flex items-center gap-2 mb-3">
                <Zap className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-zinc-700 font-['Outfit']">Needs Your Attention</span>
            </div>
            {data.items.map((item, i) => {
                const style = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.low;
                return (
                    <div key={i}
                        className={`${style.bg} border rounded-xl p-3.5 flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow`}
                        onClick={() => navigate(item.link)}
                        data-testid={`action-item-${item.action}`}>
                        <AlertCircle className={`w-4 h-4 ${style.icon} shrink-0`} />
                        <span className="text-sm text-zinc-800 flex-1">{item.label}</span>
                        <Badge className={`${style.badge} text-[10px]`}>{item.priority}</Badge>
                        <ArrowRight className="w-3.5 h-3.5 text-zinc-400" />
                    </div>
                );
            })}
        </div>
    );
}

export function ActivityFeed({ limit = 10 }) {
    const [items, setItems] = useState([]);

    useEffect(() => {
        API.get('/autonomy/feed').then(r => setItems(r.data.slice(0, limit))).catch(() => {});
    }, [limit]);

    if (items.length === 0) {
        return (
            <div className="bg-white rounded-xl border border-zinc-200 p-6 shadow-sm" data-testid="activity-feed-empty">
                <div className="flex items-center gap-2 mb-3">
                    <Bell className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm font-medium text-zinc-700 font-['Outfit']">Activity Feed</span>
                </div>
                <p className="text-sm text-zinc-400 text-center py-4">No recent activity. Actions will appear here as they happen.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-sm" data-testid="activity-feed">
            <div className="flex items-center gap-2 mb-4">
                <Bell className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-700 font-['Outfit']">Recent Activity</span>
                <span className="text-[10px] text-zinc-400 ml-auto">{items.length} events</span>
            </div>
            <div className="space-y-3">
                {items.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <span className="text-zinc-800">{item.detail}</span>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-zinc-400">{item.user_name}</span>
                                <Badge className="bg-zinc-100 text-zinc-500 text-[9px]">{item.role}</Badge>
                                <span className="text-[10px] text-zinc-400">{formatTimeAgo(item.created_at)}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function formatTimeAgo(iso) {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}
